// Health status configuration
export const HEALTH_STATUS = {
  GREEN: 'green',
  YELLOW: 'yellow', 
  RED: 'red',
  GRAY: 'gray',
  UNKNOWN: 'unknown'
}

// Health status color mapping
export const HEALTH_COLORS = {
  [HEALTH_STATUS.GREEN]: '#2ecc71',    // Healthy
  [HEALTH_STATUS.YELLOW]: '#f39c12',   // Warning
  [HEALTH_STATUS.RED]: '#e74c3c',      // Critical
  [HEALTH_STATUS.GRAY]: '#95a5a6',     // Offline/Inactive
  [HEALTH_STATUS.UNKNOWN]: '#34495e'   // Unknown
}

// Health status labels (used internally)
const HEALTH_LABELS = {
  [HEALTH_STATUS.GREEN]: 'Healthy',
  [HEALTH_STATUS.YELLOW]: 'Warning',
  [HEALTH_STATUS.RED]: 'Critical',
  [HEALTH_STATUS.GRAY]: 'Offline',
  [HEALTH_STATUS.UNKNOWN]: 'Unknown'
}

// Health status hierarchy for determining worst status
const HEALTH_HIERARCHY = [
  HEALTH_STATUS.RED,
  HEALTH_STATUS.YELLOW,
  HEALTH_STATUS.GRAY,
  HEALTH_STATUS.UNKNOWN,
  HEALTH_STATUS.GREEN
]

/**
 * Normalizes a status string by converting to lowercase and removing whitespace
 */
const normalizeStatus = (status) => {
  if (!status) return HEALTH_STATUS.UNKNOWN
  return status.toLowerCase().trim().replace(/\s+/g, '')
}

/**
 * Determines health status from modality data with fallback logic
 */
export const getHealthStatus = (modality) => {
  // Primary: Use healthStatusColor field
  if (modality.healthStatusColor) {
    return normalizeStatus(modality.healthStatusColor)
  }
  
  // Secondary: Use healthStatusLevel field
  if (modality.healthStatusLevel !== undefined) {
    const levelMap = {
      0: HEALTH_STATUS.GREEN,
      1: HEALTH_STATUS.YELLOW,
      2: HEALTH_STATUS.RED
    }
    return levelMap[modality.healthStatusLevel] || HEALTH_STATUS.UNKNOWN
  }
  
  // Tertiary: Use active field
  if (modality.active !== undefined) {
    return modality.active ? HEALTH_STATUS.GREEN : HEALTH_STATUS.GRAY
  }
  
  return HEALTH_STATUS.UNKNOWN
}

/**
 * Gets the appropriate color for a single modality
 */
export const getPinColor = (modality) => {
  const healthStatus = getHealthStatus(modality)
  return HEALTH_COLORS[healthStatus] || HEALTH_COLORS[HEALTH_STATUS.UNKNOWN]
}

/**
 * Determines the worst health status from multiple modalities
 */
export const getWorstHealthStatus = (modalities) => {
  if (!modalities?.length) return HEALTH_STATUS.UNKNOWN
  
  // Check for each status in hierarchy order (worst to best)
  for (const status of HEALTH_HIERARCHY) {
    const hasStatus = modalities.some(modality => getHealthStatus(modality) === status)
    if (hasStatus) return status
  }
  
  return HEALTH_STATUS.GREEN
}

/**
 * Gets the color for the worst health status among multiple modalities
 */
export const getWorstPinColor = (modalities) => {
  const worstStatus = getWorstHealthStatus(modalities)
  return HEALTH_COLORS[worstStatus] || HEALTH_COLORS[HEALTH_STATUS.UNKNOWN]
}

/**
 * Groups modalities by exact coordinates
 */
export const groupModalitiesByLocation = (modalities) => {
  const locationGroups = {}
  
  modalities.forEach(modality => {
    if (modality.latitude && modality.longitude) {
      const key = `${modality.latitude},${modality.longitude}`
      
      if (!locationGroups[key]) {
        locationGroups[key] = {
          latitude: modality.latitude,
          longitude: modality.longitude,
          modalities: [],
          worstStatus: HEALTH_STATUS.UNKNOWN
        }
      }
      locationGroups[key].modalities.push(modality)
    }
  })
  
  // Calculate worst status for each group
  Object.values(locationGroups).forEach(group => {
    group.worstStatus = getWorstHealthStatus(group.modalities)
  })
  
  return locationGroups
}

/**
 * Gets human-readable label for health status
 */
export const getHealthLabel = (status) => {
  return HEALTH_LABELS[status] || HEALTH_LABELS[HEALTH_STATUS.UNKNOWN]
}

/**
 * Gets detailed health information for a modality
 */
export const getHealthDetails = (modality) => {
  const healthStatus = getHealthStatus(modality)
  
  return {
    status: healthStatus,
    level: modality.healthStatusLevel,
    color: modality.healthStatusColor,
    keys: modality.healthStatusKeys || [],
    matches: modality.healthStatusMatches || {},
    metrics: modality.metrics || '',
    timeStatus: {
      level: modality.timeStatusLevel,
      color: modality.timeStatusColor,
      message: modality.timeStatusMessage
    }
  }
}

/**
 * Utility function to check if a status is considered "healthy"
 */
export const isHealthyStatus = (status) => {
  return status === HEALTH_STATUS.GREEN
}

/**
 * Utility function to check if a status requires attention
 */
export const requiresAttention = (status) => {
  return status === HEALTH_STATUS.YELLOW || status === HEALTH_STATUS.RED
}
